// TelcoTrack – finance souhrn + plán vs skutečnost (PPD)

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import {
  MaterialsUsedPageResponse,
  ObjectService,
} from '../../../core/services/object.service';
import { PolygonService } from '../../../core/services/polygon.service';
import type {
  FinanceComparisonResult,
  FinanceMaterialComparisonLine,
} from '../../../core/models/object.model';
import type { DataTableColumn } from '../ui/data-table/data-table.types';
import {
  buildFinanceProfitabilityChartRows,
  type FinanceProfitabilityChartRow,
} from '../../utils/finance-profitability-chart.util';
import {
  buildFinanceProgressComparison,
  type FinanceProgressComparisonModel,
} from '../../utils/finance-progress-comparison.util';
import {
  buildFinanceComparisonTableRows,
  type FinanceComparisonTableRow,
} from '../../utils/finance-comparison-table.util';
import type {
  FinanceWorkTableMontazSubcategoryId,
  FinanceWorkTableStavbaSubcategoryId,
  FinanceWorkTableTopCategoryId,
} from '@core/shared-bridge';
import { resolveFinanceAsOfDateIso } from '../../utils/date-filter.util';
import { buildDataTableExportAoA } from '../ui/data-table/data-table-export.util';
import {
  buildFinanceChartExportSheets,
  chartExportStamp,
  downloadChartWorkbook,
} from '../../utils/chart-dashboard-export.util';
import { ToastService } from '../ui/toast/toast.service';
const FINANCE_PLAN_HDR = 'bg-sky-100/90 dark:bg-sky-950/40 text-sky-900 dark:text-sky-100';
const FINANCE_PLAN_HDR_START = `${FINANCE_PLAN_HDR} border-l-2 border-sky-300 dark:border-sky-600`;
const FINANCE_PLAN_CELL = 'tabular-nums bg-sky-50/70 dark:bg-sky-950/25 text-sky-950 dark:text-sky-50';
const FINANCE_PLAN_CELL_START = `${FINANCE_PLAN_CELL} border-l-2 border-sky-300 dark:border-sky-600`;
const FINANCE_ACTUAL_HDR = 'bg-emerald-100/80 dark:bg-emerald-950/35 text-emerald-900 dark:text-emerald-100';
const FINANCE_ACTUAL_HDR_START = `${FINANCE_ACTUAL_HDR} border-l-2 border-emerald-400 dark:border-emerald-600`;
const FINANCE_ACTUAL_CELL = 'tabular-nums bg-emerald-50/60 dark:bg-emerald-950/20 text-emerald-950 dark:text-emerald-50';
const FINANCE_ACTUAL_CELL_START = `${FINANCE_ACTUAL_CELL} border-l-2 border-emerald-400 dark:border-emerald-600`;
const FINANCE_VARIANCE_HDR = '';
const FINANCE_VARIANCE_CELL = 'tabular-nums font-medium';

@Component({
  selector: 'app-finance-summary',
  templateUrl: './finance-summary.component.html',
})
export class FinanceSummaryComponent implements OnInit, OnChanges, OnDestroy {
  private readonly toast = inject(ToastService);

  private readonly destroy$ = new Subject<void>();
  private readonly refreshCancel$ = new Subject<void>();
  @Input() polygonId: string | null = null;
  /**
   * Portfolio view (no polygonId): when true, restrict totals to plan_status stavba
   * (`stavbaPolygonOnly` on finance / materials / work-report APIs).
   */
  @Input() stavbaPolygonOnly = false;
  /** Volitelný override hotovosti (jinak `GET …/progress-snapshot`). */
  @Input() progressByTypeAndPhase: Record<string, Record<string, number>> | null = null;
  /** Preferovaná hotovost po kategoriích (včetně milníků). */
  @Input() completionByCategory: Array<{
    id: string;
    label: string;
    completionPercent: number;
    objectCount: number;
  }> | null = null;
  /** ISO `YYYY-MM-DD` — skutečnost kumulativně k tomuto dni; prázdné = bez filtru (polygon dashboard). */
  @Input() asOfDate: string | null = null;
  /**
   * Ikona Exportovat na řádku podtitulku. Polygon ji nechává vypnutou a stahuje stejný sešit
   * z existujícího menu Export.
   */
  @Input() showHeaderExport = true;
  @Output() workReportClick = new EventEmitter<void>();
  @Output() materialsClick = new EventEmitter<void>();
  @Output() loadingChange = new EventEmitter<boolean>();

  materialsTotal = 0;
  workReportTotal = 0;
  loading = false;
  error: string | null = null;

  comparison: FinanceComparisonResult | null = null;
  comparisonLoading = false;

  profitabilityChartRows: FinanceProfitabilityChartRow[] = [];
  progressComparison: FinanceProgressComparisonModel | null = null;
  progressSnapshotLoading = false;
  progressSnapshotError: string | null = null;
  private fetchedProgressByTypeAndPhase: Record<string, Record<string, number>> | null = null;
  private fetchedCompletionByCategory: Array<{
    id: string;
    label: string;
    completionPercent: number;
    objectCount: number;
  }> | null = null;

  workLines: FinanceMaterialComparisonLine[] = [];
  materialLines: FinanceMaterialComparisonLine[] = [];
  workDetailWarning: string | null = null;
  financeExporting = false;

  workGroupExpanded = false;
  materialGroupExpanded = false;
  expandedWorkTableCategories = new Set<FinanceWorkTableTopCategoryId>();
  expandedStavbaSubcategories = new Set<FinanceWorkTableStavbaSubcategoryId>();
  expandedMontazSubcategories = new Set<FinanceWorkTableMontazSubcategoryId>();
  expandedMaterialBucketIds = new Set<string>();
  /** Rozbalené uzly stromu katalogu pod bucketem materiálu. */
  expandedMaterialTreeIds = new Set<string>();

  constructor(
    private readonly objectService: ObjectService,
    private readonly polygonService: PolygonService,
  ) {}

  private initialized = false;

  ngOnInit(): void {
    this.initialized = true;
    queueMicrotask(() => this.refresh());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized) return;
    if ('polygonId' in changes || 'asOfDate' in changes || 'stavbaPolygonOnly' in changes) {
      this.refresh();
      return;
    }
    if ('progressByTypeAndPhase' in changes || 'completionByCategory' in changes) {
      if (this.comparison) this.rebuildProfitabilityChart();
    }
  }

  ngOnDestroy(): void {
    this.refreshCancel$.next();
    this.refreshCancel$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get financeExportDisabled(): boolean {
    if (this.financeExporting) return true;
    return !this.progressComparison && !this.comparison;
  }

  /** Donuty, sloupce kategorií, milník a řádky tabulky plán vs skutečnost. */
  exportWorkbook(fileBase = 'finance-srovnani'): void {
    if (this.financeExporting) return;
    const tableAoa = this.comparison
      ? buildDataTableExportAoA(this.comparisonTableColumns, this.comparisonTableRows)
      : [];
    const sheets = buildFinanceChartExportSheets(this.progressComparison, tableAoa);
    if (sheets.length === 0) {
      this.toast.showInfo('Žádná finanční data k exportu.');
      return;
    }
    const base = fileBase.replace(/\.xlsx$/i, '');
    this.financeExporting = true;
    void downloadChartWorkbook(`${base}-${chartExportStamp()}.xlsx`, sheets)
      .catch((error) => {
        console.error('Finance Excel export failed', error);
        this.toast.showError('Nepodařilo se exportovat finance do Excelu.');
      })
      .finally(() => {
        this.financeExporting = false;
      });
  }

  get comparisonTableColumns(): DataTableColumn<FinanceComparisonTableRow>[] {
    return [
      {
        id: 'label',
        label: 'Položka',
        cell: 'template',
        format: (row) => row.label,
        headerClass: 'max-w-[14rem]',
        cellClass:
          'max-w-[14rem] break-words whitespace-normal text-neutral-800 dark:text-neutral-200',
      },
      {
        id: 'plannedQty',
        label: 'Množství',
        align: 'right',
        format: (row) => this.formatComparisonQuantity(row, 'planned'),
        headerClass: FINANCE_PLAN_HDR_START,
        cellClass: FINANCE_PLAN_CELL_START,
      },
      {
        id: 'planned',
        label: 'Plán (PPD)',
        align: 'right',
        cell: 'template',
        format: (row) => this.formatFinanceKc(row.planned),
        headerClass: FINANCE_PLAN_HDR,
        cellClass: FINANCE_PLAN_CELL,
      },
      {
        id: 'actualQty',
        label: 'Množství',
        align: 'right',
        format: (row) => this.formatComparisonQuantity(row, 'actual'),
        headerClass: FINANCE_ACTUAL_HDR_START,
        cellClass: FINANCE_ACTUAL_CELL_START,
      },
      {
        id: 'actual',
        label: 'Skutečnost',
        align: 'right',
        cell: 'template',
        format: (row) => this.formatFinanceKc(row.actual),
        headerClass: FINANCE_ACTUAL_HDR,
        cellClass: FINANCE_ACTUAL_CELL,
      },
      {
        id: 'variance',
        label: 'Rozdíl',
        align: 'right',
        cell: 'template',
        format: (row) => this.formatFinanceKc(row.variance),
        headerClass: FINANCE_VARIANCE_HDR,
        cellClass: FINANCE_VARIANCE_CELL,
      },
    ];
  }

  get comparisonTableRows(): FinanceComparisonTableRow[] {
    const c = this.comparison;
    if (!c) return [];
    return buildFinanceComparisonTableRows({
      comparison: c,
      workGroupExpanded: this.workGroupExpanded,
      expandedWorkCategories: this.expandedWorkTableCategories,
      expandedStavbaSubcategories: this.expandedStavbaSubcategories,
      expandedMontazSubcategories: this.expandedMontazSubcategories,
      materialGroupExpanded: this.materialGroupExpanded,
      expandedMaterialBucketIds: this.expandedMaterialBucketIds,
      expandedMaterialTreeIds: this.expandedMaterialTreeIds,
      workLines: this.workLines,
      materialLines: this.materialLines,
    });
  }

  isComparisonRowExpanded(row: FinanceComparisonTableRow): boolean {
    if (row.id === 'work-group') return this.workGroupExpanded;
    if (row.id === 'material-group') return this.materialGroupExpanded;
    if (row.rowType === 'category' && row.kind === 'work') {
      const catId = row.id.replace(/^work-cat-/, '') as FinanceWorkTableTopCategoryId;
      return this.expandedWorkTableCategories.has(catId);
    }
    if (row.rowType === 'subcategory' && row.kind === 'work') {
      if (row.id.startsWith('work-sub-stavba-')) {
        const subId = row.id.replace(/^work-sub-stavba-/, '') as FinanceWorkTableStavbaSubcategoryId;
        return this.expandedStavbaSubcategories.has(subId);
      }
      if (row.id.startsWith('work-sub-montaz-')) {
        const subId = row.id.replace(/^work-sub-montaz-/, '') as FinanceWorkTableMontazSubcategoryId;
        return this.expandedMontazSubcategories.has(subId);
      }
    }
    if (row.rowType === 'category' && row.kind === 'material') {
      return this.expandedMaterialBucketIds.has(row.id);
    }
    if (row.rowType === 'subcategory' && row.kind === 'material') {
      return this.expandedMaterialTreeIds.has(row.id);
    }
    return false;
  }

  labelIndentClass(row: FinanceComparisonTableRow): string {
    const depth = row.depth ?? 0;
    if (depth >= 5) return 'pl-[7.5rem]';
    if (depth >= 4) return 'pl-24';
    if (depth >= 3) return 'pl-[4.5rem]';
    if (depth >= 2) return 'pl-12';
    if (depth >= 1) return 'pl-6';
    return '';
  }

  comparisonRowClass(row: FinanceComparisonTableRow): string {
    const base = row.isTotal ? 'bg-neutral-50/80 dark:bg-neutral-800/40 font-medium' : '';
    const clickable =
      row.expandable || row.rowType === 'group' || row.rowType === 'category' || row.rowType === 'subcategory';
    const tone =
      row.kind === 'material' && row.rowType === 'group'
        ? 'text-sky-950 dark:text-sky-50'
        : row.kind === 'work' && row.rowType === 'group'
          ? 'text-emerald-950 dark:text-emerald-50'
          : 'text-neutral-800 dark:text-neutral-200';
    return `${base} ${tone} ${clickable ? 'cursor-pointer hover:bg-background-tertiary' : ''}`.trim();
  }

  onComparisonRowClick(row: FinanceComparisonTableRow): void {
    if (row.rowType === 'group') {
      if (row.id === 'work-group') {
        this.workGroupExpanded = !this.workGroupExpanded;
        return;
      }
      if (row.id === 'material-group') {
        this.materialGroupExpanded = !this.materialGroupExpanded;
      }
      return;
    }

    if (row.rowType === 'category' && row.kind === 'work') {
      const catId = row.id.replace(/^work-cat-/, '') as FinanceWorkTableTopCategoryId;
      if (this.expandedWorkTableCategories.has(catId)) {
        this.expandedWorkTableCategories.delete(catId);
      } else {
        this.expandedWorkTableCategories.add(catId);
      }
      return;
    }

    if (row.rowType === 'subcategory' && row.kind === 'work') {
      if (row.id.startsWith('work-sub-stavba-')) {
        const subId = row.id.replace(/^work-sub-stavba-/, '') as FinanceWorkTableStavbaSubcategoryId;
        if (this.expandedStavbaSubcategories.has(subId)) {
          this.expandedStavbaSubcategories.delete(subId);
        } else {
          this.expandedStavbaSubcategories.add(subId);
        }
        return;
      }
      if (row.id.startsWith('work-sub-montaz-')) {
        const subId = row.id.replace(/^work-sub-montaz-/, '') as FinanceWorkTableMontazSubcategoryId;
        if (this.expandedMontazSubcategories.has(subId)) {
          this.expandedMontazSubcategories.delete(subId);
        } else {
          this.expandedMontazSubcategories.add(subId);
        }
      }
      return;
    }

    if (row.rowType === 'category' && row.kind === 'material') {
      if (this.expandedMaterialBucketIds.has(row.id)) {
        this.expandedMaterialBucketIds.delete(row.id);
      } else {
        this.expandedMaterialBucketIds.add(row.id);
      }
      return;
    }

    if (row.rowType === 'subcategory' && row.kind === 'material') {
      if (this.expandedMaterialTreeIds.has(row.id)) {
        this.expandedMaterialTreeIds.delete(row.id);
      } else {
        this.expandedMaterialTreeIds.add(row.id);
      }
    }
  }

  trackComparisonRow(_index: number, row: FinanceComparisonTableRow): string {
    return row.id;
  }

  refresh(): void {
    this.refreshCancel$.next();
    this.setLoading(true);
    this.error = null;
    this.comparison = null;
    this.profitabilityChartRows = [];
    this.progressComparison = null;
    this.fetchedProgressByTypeAndPhase = null;
    this.fetchedCompletionByCategory = null;
    this.progressSnapshotError = null;
    this.workLines = [];
    this.materialLines = [];
    this.workDetailWarning = null;
    this.workGroupExpanded = false;
    this.materialGroupExpanded = false;
    this.expandedWorkTableCategories.clear();
    this.expandedStavbaSubcategories.clear();
    this.expandedMontazSubcategories.clear();
    this.expandedMaterialBucketIds.clear();
    this.expandedMaterialTreeIds.clear();

    const polygonId = this.polygonId?.trim() || undefined;
    const asOfDateInput = this.asOfDate?.trim();
    const hasDateBounds = !!asOfDateInput;
    const { dateFrom, dateTo } = hasDateBounds
      ? resolveFinanceAsOfDateIso(asOfDateInput)
      : { dateFrom: undefined, dateTo: undefined };
    const portfolioStavba = this.stavbaPolygonOnly === true && !polygonId;
    const financeOpts = {
      dateFrom,
      dateTo,
      ...(portfolioStavba ? { stavbaPolygonOnly: true as const } : {}),
    };

    const comparison$ = this.objectService.getFinanceComparison(polygonId, financeOpts).pipe(
      catchError(() => of(null)),
    );
    const workLines$ = this.objectService.getFinanceWorkComparison(polygonId, financeOpts).pipe(
      catchError(() => of(null)),
    );
    const materialLines$ = this.objectService.getFinanceMaterialComparison(polygonId, financeOpts).pipe(
      catchError(() => of(null)),
    );

    const needsProgressFetch = !this.hasProgressInput();
    const progress$ = needsProgressFetch
      ? this.polygonService
          .getProgressSnapshot(polygonId ?? null, { dateFrom, dateTo })
          .pipe(catchError(() => of(null)))
      : of(null);

    this.comparisonLoading = true;
    if (needsProgressFetch) this.progressSnapshotLoading = true;

    forkJoin({
      materials: this.objectService
        .getMaterialsUsed({
          polygonId,
          ...(portfolioStavba ? { stavbaPolygonOnly: true } : {}),
          ...(hasDateBounds ? { dateFrom, dateTo } : { months: 120 }),
          limit: 1,
          offset: 0,
        })
        .pipe(
          catchError(() =>
            of({ items: [], totalCount: 0, aggregateLineTotal: 0 } as MaterialsUsedPageResponse),
          ),
        ),
      work: this.objectService.getWorkReportAggregate(polygonId, financeOpts).pipe(
        catchError(() => of({ totalPrice: 0, byUser: [] })),
      ),
      comparison: comparison$,
      workLines: workLines$,
      materialLines: materialLines$,
      progress: progress$,
    })
      .pipe(takeUntil(this.refreshCancel$), takeUntil(this.destroy$))
      .subscribe({
        next: ({ materials, work, comparison, workLines, materialLines, progress }) => {
          this.materialsTotal = materials.aggregateLineTotal ?? 0;
          this.workReportTotal = work.totalPrice;
          this.comparison = comparison?.hasPpd ? comparison : null;
          this.fetchedProgressByTypeAndPhase = progress?.byObjectTypeAndPhaseCode ?? null;
          this.fetchedCompletionByCategory = progress?.completionByCategory ?? null;
          this.progressSnapshotLoading = false;
          if (needsProgressFetch && !progress && !this.hasProgressInput()) {
            this.progressSnapshotError = 'Hotovost stavby se nepodařilo načíst.';
          }
          if (workLines?.hasPpd) {
            this.workLines = workLines.lines ?? [];
            this.workDetailWarning = workLines.detailWarning?.trim() || null;
          }
          if (materialLines?.hasPpd) {
            this.materialLines = materialLines.lines ?? [];
          }
          this.rebuildProfitabilityChart();
          this.setLoading(false);
          this.comparisonLoading = false;
          this.error = null;
        },
        error: () => {
          this.materialsTotal = 0;
          this.workReportTotal = 0;
          this.comparison = null;
          this.profitabilityChartRows = [];
          this.progressComparison = null;
          this.fetchedProgressByTypeAndPhase = null;
          this.fetchedCompletionByCategory = null;
          this.progressSnapshotLoading = false;
          this.setLoading(false);
          this.comparisonLoading = false;
          this.error = 'Finance se nepodařilo načíst (materiál nebo výkaz).';
        },
      });
  }

  private hasProgressInput(): boolean {
    if (this.completionByCategory && this.completionByCategory.length > 0) return true;
    const input = this.progressByTypeAndPhase;
    return !!input && Object.keys(input).length > 0;
  }

  private effectiveProgress(): Record<string, Record<string, number>> | null {
    if (this.progressByTypeAndPhase && Object.keys(this.progressByTypeAndPhase).length > 0) {
      return this.progressByTypeAndPhase;
    }
    return this.fetchedProgressByTypeAndPhase;
  }

  private effectiveCompletionByCategory(): Array<{
    id: string;
    label: string;
    completionPercent: number;
    objectCount: number;
  }> | null {
    if (this.completionByCategory && this.completionByCategory.length > 0) {
      return this.completionByCategory;
    }
    return this.fetchedCompletionByCategory;
  }

  private rebuildProfitabilityChart(): void {
    if (!this.comparison) {
      this.profitabilityChartRows = [];
      this.progressComparison = null;
      return;
    }
    this.profitabilityChartRows = buildFinanceProfitabilityChartRows({
      comparison: this.comparison,
      progressByTypeAndPhase: this.effectiveProgress() ?? undefined,
      completionByCategory: this.effectiveCompletionByCategory() ?? undefined,
    });
    this.progressComparison = buildFinanceProgressComparison({
      comparison: this.comparison,
      rows: this.profitabilityChartRows,
      progressKnown: this.isProgressKnown(),
    });
  }

  /** Hotovost stavby je známá, i když je 0 %. Chybějící snapshot není nula. */
  private isProgressKnown(): boolean {
    if (this.hasProgressInput()) return true;
    if (this.fetchedCompletionByCategory) return true;
    return !!this.fetchedProgressByTypeAndPhase && Object.keys(this.fetchedProgressByTypeAndPhase).length > 0;
  }

  private setLoading(value: boolean): void {
    this.loading = value;
    queueMicrotask(() => this.loadingChange.emit(this.loading));
  }

  /** Skupina a řádek Celkem — souhrn, ne jednotlivá SAP položka. */
  isFinanceHeadlineRow(row: FinanceComparisonTableRow): boolean {
    return !!row.isTotal || row.rowType === 'total' || row.rowType === 'group';
  }

  formatFinanceKc(value: number | null | undefined): string {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return (
      new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + ' Kč'
    );
  }

  formatQuantityWithUnit(quantity: number | null | undefined, unit: string | null | undefined): string {
    const n = Number(quantity);
    if (!Number.isFinite(n)) return '—';
    const qtyText = new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 4 }).format(n);
    const u = unit?.trim();
    return u ? `${qtyText} ${u}` : qtyText;
  }

  formatComparisonQuantity(row: FinanceComparisonTableRow, kind: 'planned' | 'actual'): string {
    if (row.rowType === 'group' || row.rowType === 'total' || row.rowType === 'listedSum') return '';
    const qty = kind === 'planned' ? row.plannedQuantity : row.actualQuantity;
    if (qty == null) return '—';
    return this.formatQuantityWithUnit(qty, row.unit);
  }
}
